import { useEffect, useRef } from "react";
import { registerDismissLayer } from "../../../utils/dismissLayers";

/**
 * Standard accessible modal dismissal: closes on Escape, or on a pointer-down
 * outside the dialog element. Avoids attaching a click handler directly to
 * the non-focusable, non-interactive overlay backdrop — the pattern a11y
 * linters flag ("visible non-interactive element with a click handler needs
 * a keyboard listener") — by listening at the document level instead.
 *
 * While open, the modal also registers as a dismiss layer, which is what
 * keeps DeckorateModule's capture-phase Escape/Backspace ladder from
 * navigating to the home grid out from under it (utils/dismissLayers.ts).
 */
export function useModalDismiss<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const dialogRef = useRef<T>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    const releaseLayer = registerDismissLayer();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      releaseLayer();
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  return dialogRef;
}
